<script setup>
import { computed, onBeforeUnmount, reactive, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { cortexProcessingRunsAPI } from 'dashboard/api/cortex';
import { useAlert } from 'dashboard/composables';
import { cortexProcessingErrorMessage } from 'dashboard/helper/cortexProcessingError';
import AdaptiveSelect from 'dashboard/components-next/adaptive-select/AdaptiveSelect.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import PaginationFooter from 'dashboard/components-next/pagination/PaginationFooter.vue';
import BaseTable from 'dashboard/components-next/table/BaseTable.vue';
import BaseTableCell from 'dashboard/components-next/table/BaseTableCell.vue';
import BaseTableRow from 'dashboard/components-next/table/BaseTableRow.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import WithLabel from 'v3/components/Form/WithLabel.vue';

const emit = defineEmits(['saved']);
const { t } = useI18n();
const dialog = ref(null);
const run = ref(null);
const attempts = ref([]);
const selected = ref(null);
const loading = ref(false);
const saving = ref(false);
const stale = ref(false);
const errorMessage = ref('');
const page = ref(1);
const total = ref(0);
const perPage = 10;
let loadVersion = 0;
let operation;
const emptyForm = () => ({
  confirmed_cost: '',
  evidence_type: 'provider_usage',
  evidence_reference: '',
  reason: '',
  billing_confirmed: false,
});
const form = reactive(emptyForm());
const evidenceOptions = computed(() => [
  {
    value: 'provider_usage',
    label: t('CORTEX_BUDGET_RECONCILIATION.EVIDENCE_TYPES.provider_usage'),
  },
  {
    value: 'provider_invoice',
    label: t('CORTEX_BUDGET_RECONCILIATION.EVIDENCE_TYPES.provider_invoice'),
  },
  {
    value: 'provider_confirmation',
    label: t(
      'CORTEX_BUDGET_RECONCILIATION.EVIDENCE_TYPES.provider_confirmation'
    ),
  },
]);
const headers = computed(() => [
  t('CORTEX_BUDGET_RECONCILIATION.ATTEMPT'),
  t('CORTEX_BUDGET_RECONCILIATION.MODEL'),
  t('CORTEX_BUDGET_RECONCILIATION.STATE'),
  t('CORTEX_BUDGET_RECONCILIATION.RESERVED'),
  t('CORTEX_BUDGET_RECONCILIATION.ACTIONS'),
]);
const stateLabels = computed(() => ({
  reserved: t('CORTEX_BUDGET_RECONCILIATION.STATES.reserved'),
  uncertain: t('CORTEX_BUDGET_RECONCILIATION.STATES.uncertain'),
}));
const canSave = computed(() => {
  const amount = String(form.confirmed_cost).trim();
  return Boolean(
    run.value?.can_manage_budget &&
      selected.value &&
      !loading.value &&
      !saving.value &&
      !stale.value &&
      /^\d+(?:\.\d{1,6})?$/.test(amount) &&
      Number(amount) < 1_000_000 &&
      evidenceOptions.value.some(
        option => option.value === form.evidence_type
      ) &&
      form.evidence_reference.trim().length >= 5 &&
      form.evidence_reference.trim().length <= 500 &&
      form.reason.trim().length >= 10 &&
      form.reason.trim().length <= 500 &&
      form.billing_confirmed
  );
});
const formatCost = value =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 6,
  }).format(Number(value));
const formatDate = value =>
  new Intl.DateTimeFormat(undefined, {
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(value));

const loadAttempts = async (nextPage = 1) => {
  const runId = run.value?.id;
  if (!runId) return;
  loadVersion += 1;
  const version = loadVersion;
  loading.value = true;
  errorMessage.value = '';
  selected.value = null;
  operation = null;
  try {
    const { data, headers: responseHeaders } =
      await cortexProcessingRunsAPI.budgetAttempts(runId, {
        page: nextPage,
        per_page: perPage,
      });
    if (version !== loadVersion || run.value?.id !== runId) return;
    total.value = Number(responseHeaders['x-total-count'] || 0);
    const lastPage = Math.max(1, Math.ceil(total.value / perPage));
    if (nextPage > lastPage) {
      await loadAttempts(lastPage);
      return;
    }
    page.value = nextPage;
    attempts.value = data.items;
    run.value = { ...run.value, ...data.run };
    stale.value = false;
  } catch (error) {
    if (version === loadVersion)
      errorMessage.value = cortexProcessingErrorMessage(
        error,
        t,
        t('CORTEX_BUDGET_RECONCILIATION.ERROR')
      );
  } finally {
    if (version === loadVersion) loading.value = false;
  }
};

const selectAttempt = attempt => {
  selected.value = attempt;
  Object.assign(form, emptyForm());
  errorMessage.value = '';
  stale.value = false;
  operation = null;
};
const open = async selectedRun => {
  if (saving.value || !selectedRun.can_manage_budget) return;
  run.value = { ...selectedRun };
  attempts.value = [];
  total.value = 0;
  page.value = 1;
  dialog.value?.open();
  await loadAttempts();
};
const close = () => {
  loadVersion += 1;
  run.value = null;
  selected.value = null;
  loading.value = false;
};
onBeforeUnmount(close);

const save = async () => {
  if (!canSave.value) return;
  const runId = run.value.id;
  const attemptId = selected.value.id;
  const data = {
    ...form,
    confirmed_cost: String(form.confirmed_cost).trim(),
    evidence_reference: form.evidence_reference.trim(),
    reason: form.reason.trim(),
    lock_version: run.value.lock_version,
  };
  const signature = JSON.stringify({ runId, attemptId, ...data });
  if (operation?.signature !== signature)
    operation = {
      signature,
      key: `reconcile-${crypto.getRandomValues(new Uint32Array(4)).join('-')}`,
    };
  saving.value = true;
  errorMessage.value = '';
  try {
    await cortexProcessingRunsAPI.reconcileBudgetAttempt(
      runId,
      attemptId,
      data,
      operation.key
    );
    emit('saved', runId);
    useAlert(t('CORTEX_BUDGET_RECONCILIATION.SUCCESS'));
    if (run.value?.id === runId) await loadAttempts();
  } catch (error) {
    stale.value = error?.response?.status === 409;
    errorMessage.value = cortexProcessingErrorMessage(
      error,
      t,
      stale.value
        ? t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.STALE')
        : t('CORTEX_BUDGET_RECONCILIATION.ERROR')
    );
  } finally {
    saving.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    width="2xl"
    overflow-y-auto
    :title="$t('CORTEX_BUDGET_RECONCILIATION.TITLE', { id: run?.id })"
    :description="$t('CORTEX_BUDGET_RECONCILIATION.DESCRIPTION')"
    :show-confirm-button="Boolean(selected)"
    :confirm-button-label="$t('CORTEX_BUDGET_RECONCILIATION.CONFIRM')"
    confirm-button-icon="i-lucide-check"
    cancel-button-icon="i-lucide-x"
    :disable-confirm-button="!canSave"
    :is-loading="saving"
    @confirm="save"
    @close="close"
  >
    <div v-if="run" class="grid min-w-0 gap-4">
      <p v-if="errorMessage" role="alert" class="text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
      <NextButton
        v-if="!selected || stale"
        type="button"
        slate
        ghost
        icon="i-lucide-refresh-cw"
        :label="$t('CORTEX.PROCESSING.V6.REFRESH')"
        :is-loading="loading"
        :disabled="saving"
        class="justify-self-end"
        @click="loadAttempts(page)"
      />
      <template v-if="!selected">
        <BaseTable
          :headers="headers"
          :items="attempts"
          :loading="loading"
          :loading-message="$t('CORTEX_BUDGET_RECONCILIATION.LOADING')"
          :no-data-message="$t('CORTEX_BUDGET_RECONCILIATION.EMPTY')"
        >
          <template #row>
            <BaseTableRow
              v-for="attempt in attempts"
              :key="attempt.id"
              :item="attempt"
              class="align-middle"
            >
              <BaseTableCell>
                <div>
                  {{
                    $t('CORTEX_BUDGET_RECONCILIATION.ATTEMPT_ID', {
                      id: attempt.id,
                    })
                  }}
                </div>
                <div class="text-label-small leading-4 text-n-slate-11">
                  {{ formatDate(attempt.created_at) }}
                </div>
              </BaseTableCell>
              <BaseTableCell>
                <div class="break-words">{{ attempt.model }}</div>
                <div class="text-label-small leading-4 text-n-slate-11">
                  {{ attempt.provider }}
                </div>
              </BaseTableCell>
              <BaseTableCell>
                {{ stateLabels[attempt.state] }}
              </BaseTableCell>
              <BaseTableCell>
                {{ formatCost(attempt.reserved_cost) }}
              </BaseTableCell>
              <BaseTableCell align="end">
                <NextButton
                  type="button"
                  sm
                  ghost
                  icon="i-lucide-receipt-text"
                  :label="$t('CORTEX_BUDGET_RECONCILIATION.REVIEW')"
                  @click="selectAttempt(attempt)"
                />
              </BaseTableCell>
            </BaseTableRow>
          </template>
        </BaseTable>
        <PaginationFooter
          :current-page="page"
          :total-items="total"
          :items-per-page="perPage"
          @update:current-page="loadAttempts"
        />
      </template>
      <div v-else class="grid min-w-0 gap-4 sm:grid-cols-2">
        <NextButton
          type="button"
          slate
          ghost
          icon="i-lucide-arrow-left"
          :label="$t('CORTEX_BUDGET_RECONCILIATION.BACK')"
          :disabled="saving"
          class="justify-self-start sm:col-span-2"
          @click="selected = null"
        />
        <p class="text-label-small leading-4 text-n-slate-11 sm:col-span-2">
          {{
            $t('CORTEX_BUDGET_RECONCILIATION.SELECTED', {
              id: selected.id,
              amount: formatCost(selected.reserved_cost),
            })
          }}
        </p>
        <NextInput
          v-model="form.confirmed_cost"
          type="number"
          min="0"
          max="999999.999999"
          step="0.000001"
          :label="$t('CORTEX_BUDGET_RECONCILIATION.COST')"
          :message="$t('CORTEX_BUDGET_RECONCILIATION.COST_HELP')"
          :disabled="saving"
        />
        <WithLabel
          name="budget-evidence-type"
          :label="$t('CORTEX_BUDGET_RECONCILIATION.EVIDENCE_TYPE')"
        >
          <AdaptiveSelect
            id="budget-evidence-type"
            v-model="form.evidence_type"
            :options="evidenceOptions"
            :disabled="saving"
            :aria-label="$t('CORTEX_BUDGET_RECONCILIATION.EVIDENCE_TYPE')"
          />
        </WithLabel>
        <NextInput
          v-model="form.evidence_reference"
          :label="$t('CORTEX_BUDGET_RECONCILIATION.REFERENCE')"
          :message="$t('CORTEX_BUDGET_RECONCILIATION.REFERENCE_HELP')"
          :disabled="saving"
          class="sm:col-span-2"
        />
        <TextArea
          id="budget-reconciliation-reason"
          v-model="form.reason"
          :label="$t('CORTEX_BUDGET_RECONCILIATION.REASON')"
          :message="$t('CORTEX_BUDGET_RECONCILIATION.REASON_HELP')"
          :max-length="500"
          :disabled="saving"
          class="sm:col-span-2"
        />
        <label
          class="flex cursor-pointer items-start gap-2.5 text-sm text-n-slate-12 sm:col-span-2"
        >
          <NextCheckbox
            v-model="form.billing_confirmed"
            :disabled="saving"
            class="mt-0.5"
          />
          <span>{{ $t('CORTEX_BUDGET_RECONCILIATION.ACKNOWLEDGEMENT') }}</span>
        </label>
      </div>
    </div>
  </Dialog>
</template>
